import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ScrollText } from 'lucide-react'
import {
  AUDIT_ACTION_LABELS,
  AUDIT_CATEGORIES,
  AUDIT_CATEGORY_LABELS,
  AUDIT_ENTITIES,
  SECURITY_AUDIT_ACTIONS,
  type AuditAction,
  type AuditEntity,
} from '@tumin/domain'
import { DataTable, type Column } from '@/components/data-table'
import { KeyValueList } from '@/components/key-value-list'
import { PageHeader } from '@/components/page-header'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { NativeSelect } from '@/components/ui/native-select'
import { AuditDiff } from '@/features/admin/audit/audit-diff'
import type { Employee } from '@/features/admin/users/types'
import { api } from '@/lib/api'
import { formatDateTime, formatRelative } from '@/lib/dates'
import { queryKeys } from '@/lib/query-keys'
import { requirePermission } from '@/lib/route-guards'
import { useServerTable } from '@/lib/use-server-table'

export const Route = createFileRoute('/_app/admin/audit')({
  beforeLoad: requirePermission('audit:read'),
  component: AuditPage,
})

/** Evento de la bitácora (`GET /audit-logs`). */
interface AuditLogEntry {
  id: string
  action: string
  entityType: string
  entityId: string
  before: unknown
  after: unknown
  ipAddress: string | null
  createdAt: string
  employeeId: string | null
  employeeName: string | null
}

/** Nombre en español de cada tipo de entidad auditada. */
const ENTITY_LABELS: Record<AuditEntity, string> = {
  session: 'Sesión',
  employee: 'Usuario',
  settings: 'Configuración',
  branch: 'Agencia',
  product: 'Producto',
  client: 'Cliente',
  loan: 'Préstamo',
  payment: 'Pago',
  invoice: 'Factura electrónica',
  cash_session: 'Caja',
  compliance_alert: 'Alerta de cumplimiento',
  document: 'Documento',
  accounting_period: 'Período contable',
  journal_entry: 'Partida contable',
}

/** Valor de filtro "todos". */
const ANY = ''
/** Máximo de usuarios en el filtro por usuario (límite de página del API). */
const EMPLOYEE_FILTER_LIMIT = 100

function actionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action as AuditAction] ?? action
}

function entityLabel(entity: string): string {
  return ENTITY_LABELS[entity as AuditEntity] ?? entity
}

/** Acciones que no son de seguridad (grupo «Operación» del filtro). */
const OPERATION_ACTIONS = (Object.keys(AUDIT_ACTION_LABELS) as AuditAction[]).filter((a) => !SECURITY_AUDIT_ACTIONS.includes(a))

function AuditPage() {
  const [category, setCategory] = useState(ANY)
  const [entityType, setEntityType] = useState(ANY)
  const [action, setAction] = useState(ANY)
  const [employeeId, setEmployeeId] = useState(ANY)
  const [selected, setSelected] = useState<AuditLogEntry | null>(null)

  const table = useServerTable<AuditLogEntry>({
    queryKey: queryKeys.auditLogs.all,
    path: '/audit-logs',
    params: {
      category: category || undefined,
      entityType: entityType || undefined,
      action: action || undefined,
      employeeId: employeeId || undefined,
    },
  })
  const { data: employees } = useQuery({
    queryKey: queryKeys.employees.list({ pageSize: EMPLOYEE_FILTER_LIMIT }),
    queryFn: ({ signal }) => api.list<Employee>('/employees', { pageSize: EMPLOYEE_FILTER_LIMIT, sortBy: 'fullName', sortDir: 'asc' }, signal),
  })

  const columns: Column<AuditLogEntry>[] = [
    {
      id: 'createdAt',
      header: 'Fecha',
      className: 'whitespace-nowrap',
      cell: (e) => (
        <time dateTime={e.createdAt} title={formatDateTime(e.createdAt)} className="tabular text-xs">
          {formatDateTime(e.createdAt)}
        </time>
      ),
    },
    { id: 'action', header: 'Acción', cell: (e) => <span className="font-medium">{actionLabel(e.action)}</span> },
    { id: 'entity', header: 'Registro', cell: (e) => entityLabel(e.entityType) },
    { id: 'employee', header: 'Usuario', cell: (e) => e.employeeName ?? 'Sistema' },
    { id: 'ip', header: 'IP', hideOnMobile: true, className: 'tabular text-xs text-muted-foreground', cell: (e) => e.ipAddress ?? '—' },
  ]

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Administración" title="Bitácora" description="Registro inalterable de quién hizo qué y cuándo. Toque un evento para ver el antes y después." />
      <DataTable
        ariaLabel="Bitácora de auditoría"
        columns={columns}
        rows={table.rows}
        total={table.total}
        page={table.page}
        pageSize={table.pageSize}
        onPageChange={table.setPage}
        searchable={false}
        isLoading={table.isLoading}
        isFetching={table.isFetching}
        error={table.error}
        onRetry={() => void table.refetch()}
        getRowId={(e) => e.id}
        onRowClick={setSelected}
        emptyTitle="Sin eventos"
        emptyHint="No hay eventos con los filtros elegidos."
        toolbar={
          <>
            <NativeSelect aria-label="Filtrar por sección" value={category} onChange={(e) => setCategory(e.target.value)} className="w-40">
              <option value={ANY}>Todas las secciones</option>
              {AUDIT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {AUDIT_CATEGORY_LABELS[c]}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect aria-label="Filtrar por registro" value={entityType} onChange={(e) => setEntityType(e.target.value)} className="w-44">
              <option value={ANY}>Todos los registros</option>
              {Object.values(AUDIT_ENTITIES).map((entity) => (
                <option key={entity} value={entity}>
                  {entityLabel(entity)}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect aria-label="Filtrar por acción" value={action} onChange={(e) => setAction(e.target.value)} className="w-52">
              <option value={ANY}>Todas las acciones</option>
              <optgroup label={AUDIT_CATEGORY_LABELS.security}>
                {SECURITY_AUDIT_ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {AUDIT_ACTION_LABELS[a]}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Operación">
                {OPERATION_ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {AUDIT_ACTION_LABELS[a]}
                  </option>
                ))}
              </optgroup>
            </NativeSelect>
            <NativeSelect aria-label="Filtrar por usuario" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="w-48">
              <option value={ANY}>Todos los usuarios</option>
              {(employees?.data ?? []).map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.fullName}
                </option>
              ))}
            </NativeSelect>
          </>
        }
        mobileCard={(e) => (
          <div className="space-y-0.5">
            <p className="font-medium">{actionLabel(e.action)}</p>
            <p className="text-xs text-muted-foreground">
              {entityLabel(e.entityType)} · {e.employeeName ?? 'Sistema'} · {formatRelative(e.createdAt)}
            </p>
          </div>
        )}
      />

      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="size-5 text-primary" aria-hidden />
              {selected ? actionLabel(selected.action) : ''}
            </DialogTitle>
            <DialogDescription>{selected ? formatDateTime(selected.createdAt) : ''}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <KeyValueList
                items={[
                  { label: 'Usuario', value: selected.employeeName ?? 'Sistema' },
                  { label: 'Registro', value: entityLabel(selected.entityType) },
                  { label: 'Identificador', value: <span className="tabular break-all text-xs">{selected.entityId}</span> },
                  { label: 'Dirección IP', value: selected.ipAddress, numeric: true },
                ]}
              />
              <AuditDiff before={selected.before} after={selected.after} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
